import React from 'react';
import Modal from '../ui/Modal';
import Stepper from '../ui/Stepper';
import Toggle from '../ui/Toggle';
import Icon from '../ui/Icon';
import SegmentBuilder from '../audience/SegmentBuilder';
import PhonePreview from './PhonePreview';
import EventBus from '../../utils/EventBus';
import { campaignService } from '../../services/campaignService';
import { segmentService } from '../../services/segmentService';
import { filtersToConditions, toMatchType } from '../../utils/segmentConditions';
import styles from './CampaignWizard.module.css';

const STEPS = ['Audience', 'Channel', 'Message', 'Schedule', 'Review'];

const CHANNELS = [
  { key: 'push',     ico: 'notifications', name: 'Push Notification', desc: 'FCM for Android · APNs for iOS. Works even when app is closed.',       stat: '93.1% delivered', sc: 'rgba(79,155,111,.09)', tc: '#63B887', bg: 'rgba(184,66,66,.1)' },
  { key: 'inapp',    ico: 'phone_iphone',  name: 'In-App Message',    desc: 'Banner, modal or tooltip shown while the app is open. No opt-in needed.', stat: '100% delivery',   sc: 'rgba(72,120,176,.09)', tc: '#6090C8', bg: 'rgba(72,120,176,.1)' },
  { key: 'whatsapp', ico: 'forum',         name: 'WhatsApp',          desc: 'WhatsApp Business templates via Exotel · high open rates.',             stat: '~98% open rate',  sc: 'rgba(160,124,48,.09)', tc: '#C09848', bg: 'rgba(79,155,111,.1)' },
  { key: 'sms',      ico: 'sms',           name: 'SMS',               desc: 'AWS SNS text messages · DLT-compliant templates for India.',            stat: '~95% delivered',  sc: 'rgba(160,124,48,.09)', tc: '#C09848', bg: 'rgba(160,124,48,.1)' },
  { key: 'email',    ico: 'mail',          name: 'Email',             desc: 'Rich HTML emails for newsletters, digests, and re-engagement.',         stat: '34.2% open rate', sc: 'rgba(110,98,168,.09)', tc: '#8878C0', bg: 'rgba(110,98,168,.1)' },
];

const SCHEDULES = [
  { key: 'now',       ico: 'bolt',          name: 'Send Now',     desc: 'Deliver immediately after you launch' },
  { key: 'scheduled', ico: 'calendar_month',name: 'Schedule',     desc: 'Pick a specific date and time' },
  { key: 'optimal',   ico: 'auto_awesome',  name: 'Optimal Time', desc: 'AI picks the best send time per user based on their past activity', disabled: true },
];

const DEFAULT_STATE = {
  step: 0,
  editingId: null,
  editingSegmentId: null,
  editingLaunchedAt: null,
  initialSegment: null,
  audience: null,
  channel: 'push',
  msgTitle: '',
  msgBody: '',
  msgDeeplink: '',
  msgBtn1: '',
  msgBtn2: '',
  waTemplateName: '',
  waTemplateParams: '',
  waImageUrl: '',
  waIsCustomText: false,
  waText: '',
  smsText: '',
  smsTemplateId: '',
  emailSubject: '',
  emailHtml: '',
  schedType: 'now',
  schedDate: '',
  schedTime: '',
  schedTZ: 'IST +5:30',
  quietHours: true,
  stopOnConvert: true,
  freqCap: true,
  controlGroup: false,
  campName: '',
  saving: false,
};

class Step1Audience extends React.Component {
  render() {
    const { onChange, initialSegment } = this.props;
    return (
      <div>
        <p className={styles.stepIntro}>Who should receive this campaign?</p>
        <SegmentBuilder
          initialSegment={initialSegment}
          onChange={onChange}
          onSaved={(segment) => onChange({
            mode: 'saved',
            selectedSegId: segment.id,
            filters: [],
            op: 'AND',
            reach: segment.user_count,
          })}
        />
      </div>
    );
  }
}

class Step2Channel extends React.Component {
  render() {
    const { value, onChange } = this.props;
    return (
      <div>
        <p className={styles.stepIntro}>How should this message be delivered?</p>
        <div className={styles.chList}>
          {CHANNELS.map(ch => (
            <div
              key={ch.key}
              className={[styles.chRow, value === ch.key ? styles.chOn : ''].join(' ')}
              onClick={() => onChange(ch.key)}
            >
              <div className={styles.chIco} style={{ background: ch.bg }}>
                <Icon name={ch.ico} size={20} />
              </div>
              <div className={styles.chInfo}>
                <div className={styles.chName}>{ch.name}</div>
                <div className={styles.chDesc}>{ch.desc}</div>
              </div>
              <div className={styles.chStat} style={{ background: ch.sc, color: ch.tc }}>{ch.stat}</div>
              <div className={[styles.chRadio, value === ch.key ? styles.chRadioOn : ''].join(' ')} />
            </div>
          ))}
        </div>
      </div>
    );
  }
}

class PushLikeMessage extends React.Component {
  render() {
    const { title, body, deeplink, btn1, btn2, onField } = this.props;
    return (
      <div className={styles.msgLayout}>
        <div className={styles.msgFields}>
          <div className={styles.fi}>
            <label className={styles.fiLabel}>Title <span className={styles.req}>*</span></label>
            <input
              className={styles.fiInp}
              placeholder="e.g. 🛒 Your cart misses you!"
              maxLength={65}
              value={title}
              onChange={e => onField('msgTitle', e.target.value)}
            />
            <div className={styles.fiRow}>
              <span className={styles.fiHint}>Under 50 chars ideal for Android lock screen</span>
              <span className={styles.fiCount}>{title.length} / 65</span>
            </div>
          </div>
          <div className={styles.fi}>
            <label className={styles.fiLabel}>Body <span className={styles.req}>*</span></label>
            <textarea
              className={[styles.fiInp, styles.fiTa].join(' ')}
              placeholder="e.g. Complete your purchase and save 10%. Offer expires tonight!"
              maxLength={200}
              value={body}
              onChange={e => onField('msgBody', e.target.value)}
            />
            <div className={styles.fiRow}>
              <span className={styles.fiHint}>2 lines shown on lock screen</span>
              <span className={styles.fiCount}>{body.length} / 200</span>
            </div>
          </div>
          <div className={styles.fiDivider} />
          <div className={styles.fi}>
            <label className={styles.fiLabel}>On tap — open</label>
            <input
              className={styles.fiInp}
              placeholder="app://screen  or  https://…"
              value={deeplink}
              onChange={e => onField('msgDeeplink', e.target.value)}
            />
          </div>
          <div className={styles.fi2col}>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Action button 1 <span className={styles.opt}>(optional)</span></label>
              <input
                className={styles.fiInp}
                placeholder="e.g. View Cart"
                value={btn1}
                onChange={e => onField('msgBtn1', e.target.value)}
              />
            </div>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Action button 2 <span className={styles.opt}>(optional)</span></label>
              <input
                className={styles.fiInp}
                placeholder="e.g. Dismiss"
                value={btn2}
                onChange={e => onField('msgBtn2', e.target.value)}
              />
            </div>
          </div>
        </div>
        <PhonePreview title={title} body={body} />
      </div>
    );
  }
}

// WhatsApp Business messages must use a pre-approved template outside the 24h
// service window — templateName + positional params, not free-form text.
class WhatsappMessage extends React.Component {
  render() {
    const { waTemplateName, waTemplateParams, waImageUrl, waIsCustomText, waText, onField } = this.props;
    return (
      <div className={styles.msgFields}>
        <div className={styles.fi}>
          <label className={styles.fiLabel}>Send as</label>
          <select className={styles.fiSel} value={waIsCustomText ? 'custom' : 'template'} onChange={e => onField('waIsCustomText', e.target.value === 'custom')}>
            <option value="template">Approved template</option>
            <option value="custom">Free text (only works inside a 24h open service window)</option>
          </select>
        </div>
        {waIsCustomText ? (
          <div className={styles.fi}>
            <label className={styles.fiLabel}>Message <span className={styles.req}>*</span></label>
            <textarea className={[styles.fiInp, styles.fiTa].join(' ')} value={waText} onChange={e => onField('waText', e.target.value)} />
          </div>
        ) : (
          <>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Template name <span className={styles.req}>*</span></label>
              <input className={styles.fiInp} placeholder="e.g. cart_recovery_v2" value={waTemplateName} onChange={e => onField('waTemplateName', e.target.value)} />
              <div className={styles.fiHint}>Must match a template already approved in your WhatsApp Business account.</div>
            </div>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Template params <span className={styles.opt}>(comma-separated, in order)</span></label>
              <input className={styles.fiInp} placeholder="e.g. Rahul, 10%" value={waTemplateParams} onChange={e => onField('waTemplateParams', e.target.value)} />
            </div>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Header image URL <span className={styles.opt}>(optional)</span></label>
              <input className={styles.fiInp} placeholder="https://…" value={waImageUrl} onChange={e => onField('waImageUrl', e.target.value)} />
            </div>
          </>
        )}
      </div>
    );
  }
}

class SmsMessage extends React.Component {
  render() {
    const { smsText, smsTemplateId, onField } = this.props;
    return (
      <div className={styles.msgFields}>
        <div className={styles.fi}>
          <label className={styles.fiLabel}>Message <span className={styles.req}>*</span></label>
          <textarea className={[styles.fiInp, styles.fiTa].join(' ')} maxLength={160} value={smsText} onChange={e => onField('smsText', e.target.value)} />
          <div className={styles.fiHint}>Must match your registered DLT template's approved wording exactly.</div>
        </div>
        <div className={styles.fi}>
          <label className={styles.fiLabel}>DLT template ID <span className={styles.opt}>(optional)</span></label>
          <input className={styles.fiInp} value={smsTemplateId} onChange={e => onField('smsTemplateId', e.target.value)} />
        </div>
      </div>
    );
  }
}

class EmailMessage extends React.Component {
  render() {
    const { emailSubject, emailHtml, onField } = this.props;
    return (
      <div className={styles.msgFields}>
        <div className={styles.fi}>
          <label className={styles.fiLabel}>Subject <span className={styles.req}>*</span></label>
          <input className={styles.fiInp} value={emailSubject} onChange={e => onField('emailSubject', e.target.value)} />
        </div>
        <div className={styles.fi}>
          <label className={styles.fiLabel}>Body (HTML) <span className={styles.req}>*</span></label>
          <textarea className={[styles.fiInp, styles.fiTa].join(' ')} style={{ minHeight: 180, fontFamily: 'monospace' }} value={emailHtml} onChange={e => onField('emailHtml', e.target.value)} />
        </div>
      </div>
    );
  }
}

class Step3Message extends React.Component {
  render() {
    const { channel, ...rest } = this.props;
    if (channel === 'whatsapp') return <WhatsappMessage {...rest} />;
    if (channel === 'sms') return <SmsMessage {...rest} />;
    if (channel === 'email') return <EmailMessage {...rest} />;
    return <PushLikeMessage title={rest.msgTitle} body={rest.msgBody} deeplink={rest.msgDeeplink} btn1={rest.msgBtn1} btn2={rest.msgBtn2} onField={rest.onField} />;
  }
}

class Step4Schedule extends React.Component {
  render() {
    const { schedType, schedDate, schedTime, schedTZ, quietHours, stopOnConvert, freqCap, controlGroup, onField } = this.props;

    return (
      <div>
        <p className={styles.stepIntro}>When should this campaign go out?</p>
        <div className={styles.schedList}>
          {SCHEDULES.map(sc => (
            <div
              key={sc.key}
              className={[styles.schedRow, schedType === sc.key ? styles.schedOn : '', sc.disabled ? styles.schedDisabled : ''].join(' ')}
              onClick={() => !sc.disabled && onField('schedType', sc.key)}
              title={sc.disabled ? 'Coming soon' : undefined}
            >
              <div className={styles.schedIco}><Icon name={sc.ico} size={20} /></div>
              <div>
                <div className={styles.schedName}>
                  {sc.name}
                  {sc.disabled && <span className={styles.soonBadge}>Coming soon</span>}
                </div>
                <div className={styles.schedDesc}>{sc.desc}</div>
              </div>
              {!sc.disabled && (
                <div className={[styles.schedRadio, schedType === sc.key ? styles.schedRadioOn : ''].join(' ')} />
              )}
            </div>
          ))}
        </div>

        {schedType === 'scheduled' && (
          <div className={styles.schedExtra}>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Date</label>
              <input className={styles.fiInp} type="date" value={schedDate} onChange={e => onField('schedDate', e.target.value)} />
            </div>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Time</label>
              <input className={styles.fiInp} type="time" value={schedTime} onChange={e => onField('schedTime', e.target.value)} />
            </div>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Timezone</label>
              <select className={styles.fiSel} value={schedTZ} onChange={e => onField('schedTZ', e.target.value)}>
                <option>IST +5:30</option>
                <option>UTC</option>
                <option>EST −5</option>
                <option>PST −8</option>
              </select>
            </div>
          </div>
        )}

        <div className={styles.msLabel}>Delivery settings</div>
        <div className={styles.tglBlock}>
          {[
            { key: 'quietHours',    val: quietHours,    title: 'Quiet hours',          sub: 'Skip delivery between 11 PM – 8 AM IST' },
            { key: 'stopOnConvert', val: stopOnConvert, title: 'Stop on conversion',   sub: 'Don\'t send if user already completed the goal event' },
            { key: 'freqCap',       val: freqCap,       title: 'Frequency cap',        sub: 'Skip users who received a message in the last 24 hours' },
            { key: 'controlGroup',  val: controlGroup,  title: 'Control group (10%)',  sub: 'Hold out a group to measure campaign lift', disabled: true },
          ].map(t => (
            <div key={t.key} className={[styles.tglRow, t.disabled ? styles.tglDisabled : ''].join(' ')} title={t.disabled ? 'Coming soon' : undefined}>
              <div className={styles.tglInfo}>
                <div className={styles.tglTitle}>
                  {t.title}
                  {t.disabled && <span className={styles.soonBadge}>Coming soon</span>}
                </div>
                <div className={styles.tglSub}>{t.sub}</div>
              </div>
              <Toggle checked={t.disabled ? false : t.val} onChange={v => !t.disabled && onField(t.key, v)} disabled={t.disabled} />
            </div>
          ))}
        </div>
      </div>
    );
  }
}

class Step5Review extends React.Component {
  render() {
    const { campName, audience, channel, msgTitle, waTemplateName, smsText, emailSubject, schedType, onField } = this.props;

    const chInfo  = CHANNELS.find(c => c.key === channel) || CHANNELS[0];
    const scInfo  = SCHEDULES.find(s => s.key === schedType) || SCHEDULES[0];
    const messageSummary = { push: msgTitle, inapp: msgTitle, whatsapp: waTemplateName, sms: smsText, email: emailSubject }[channel] || '—';
    const summary = [
      { k: 'Audience', v: audience?.selectedSegId ? 'Saved Segment' : (audience?.filters?.length ? 'Custom filter (' + (audience?.reach || 0).toLocaleString() + ' users)' : 'Unchanged') },
      { k: 'Channel',  v: chInfo.name },
      { k: 'Message',  v: messageSummary || '—' },
      { k: 'Schedule', v: scInfo.name },
    ];

    return (
      <div>
        <p className={styles.stepIntro}>Almost done — give this campaign a name your team will recognise, then review and launch.</p>
        <div className={styles.fi}>
          <label className={styles.fiLabel} style={{ textTransform: 'uppercase', letterSpacing: '.6px' }}>
            Campaign Name <span className={styles.req}>*</span>
          </label>
          <input
            className={[styles.fiInp, styles.campNameInp].join(' ')}
            placeholder="e.g. Cart Recovery — Power Users — July 2026"
            value={campName}
            onChange={e => onField('campName', e.target.value)}
          />
          <div className={styles.fiHint} style={{ marginTop: 6 }}>Visible only to your team, not to users.</div>
        </div>

        <div className={styles.rvLabel}>Review Summary</div>
        <div className={styles.rvWrap}>
          {summary.map(r => (
            <div key={r.k} className={styles.rvRow}>
              <span className={styles.rvKey}>{r.k}</span>
              <span className={styles.rvVal}>{r.v}</span>
            </div>
          ))}
        </div>

        <div className={styles.rvEsts}>
          <div className={styles.rvEstItem}>
            <div className={styles.rvEstVal}>{((audience?.reach || 268000) * 0.931 / 1000).toFixed(1)}K</div>
            <div className={styles.rvEstLbl}>Est. Delivered</div>
          </div>
          <div className={styles.rvEstItem}>
            <div className={styles.rvEstVal}>{((audience?.reach || 268000) * 0.18 / 1000).toFixed(1)}K</div>
            <div className={styles.rvEstLbl}>Est. Clicks</div>
          </div>
          <div className={styles.rvEstItem}>
            <div className={styles.rvEstVal}>{((audience?.reach || 268000) * 0.043 / 1000).toFixed(1)}K</div>
            <div className={styles.rvEstLbl}>Est. Conversions</div>
          </div>
        </div>
      </div>
    );
  }
}

class CampaignWizard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { ...DEFAULT_STATE, open: false };
    this._openHandler = this._openHandler.bind(this);
    this._closeHandler = this._closeHandler.bind(this);
    this.next = this.next.bind(this);
    this.back = this.back.bind(this);
    this.draft = this.draft.bind(this);
    this.launch = this.launch.bind(this);
    this.setField = this.setField.bind(this);
  }

  componentDidMount() {
    EventBus.on('openWizard', this._openHandler);
    EventBus.on('closeWizard', this._closeHandler);
  }

  componentWillUnmount() {
    EventBus.off('openWizard', this._openHandler);
    EventBus.off('closeWizard', this._closeHandler);
  }

  // Editing an existing campaign: prefill every field from it. The modal only
  // opens once the segment fetch resolves — SegmentBuilder reads
  // `initialSegment` in its constructor only, so it must already be in props
  // by the time it first mounts, or the prefill silently wouldn't show.
  _openHandler(payload) {
    const campaign = payload?.campaign;
    if (!campaign) {
      this.setState({ ...DEFAULT_STATE, open: true });
      return;
    }

    this.setState({ ...DEFAULT_STATE, open: false });

    const t = campaign.template || {};
    const settings = campaign.delivery_settings || {};
    const scheduled = campaign.scheduled_at ? new Date(campaign.scheduled_at) : null;
    const prefill = {
      editingId: campaign.id,
      editingSegmentId: campaign.segment_id,
      editingLaunchedAt: campaign.launched_at,
      campName: campaign.name,
      channel: campaign.channel,
      quietHours: settings.quietHours ?? DEFAULT_STATE.quietHours,
      stopOnConvert: settings.stopOnConvert ?? DEFAULT_STATE.stopOnConvert,
      freqCap: settings.freqCap ?? DEFAULT_STATE.freqCap,
      controlGroup: settings.controlGroup ?? DEFAULT_STATE.controlGroup,
      schedType: scheduled ? 'scheduled' : 'now',
      schedDate: scheduled ? scheduled.toISOString().slice(0, 10) : '',
      schedTime: scheduled ? scheduled.toISOString().slice(11, 16) : '',
      msgTitle: t.title || '',
      msgBody: t.body || '',
      msgDeeplink: t.deeplink || '',
      msgBtn1: t.button1 || '',
      msgBtn2: t.button2 || '',
      waTemplateName: t.templateName || '',
      waTemplateParams: (t.templateParams || []).join(', '),
      waImageUrl: t.imageUrl || '',
      waIsCustomText: !!t.isCustomText,
      waText: t.text || '',
      smsText: t.text || '',
      smsTemplateId: t.templateId || '',
      emailSubject: t.subject || '',
      emailHtml: t.html || '',
    };

    segmentService.getById(campaign.segment_id)
      .then((segment) => this.setState({ ...prefill, initialSegment: segment, open: true }))
      .catch(() => this.setState({ ...prefill, open: true }));
  }

  _closeHandler() { this.setState({ open: false }); }

  setField(key, val) { this.setState({ [key]: val }); }

  next() {
    this.setState(s => ({ step: Math.min(STEPS.length - 1, s.step + 1) }));
  }

  back() {
    this.setState(s => ({ step: Math.max(0, s.step - 1) }));
  }

  // Resolves the audience picked in Step1 into a real segment_id — creating an
  // ad-hoc segment from the filter builder's conditions if the user didn't
  // pick an already-saved one. When editing and the audience step was never
  // touched, reuse the campaign's existing segment rather than creating a
  // pointless duplicate.
  async _resolveSegmentId() {
    const { audience, editingSegmentId } = this.state;
    if (!audience && editingSegmentId) return editingSegmentId;
    if (audience?.mode === 'saved' && audience.selectedSegId) {
      return audience.selectedSegId;
    }
    if (audience?.filters?.length) {
      const conditions = filtersToConditions(audience.filters);
      if (!conditions.length) return editingSegmentId || null;
      const segment = await segmentService.create({
        name: this.state.campName ? `Audience — ${this.state.campName}` : `Campaign audience — ${new Date().toISOString()}`,
        conditions,
        match_type: toMatchType(audience.op),
      });
      return segment.id;
    }
    return editingSegmentId || null;
  }

  _buildScheduledAt() {
    const { schedType, schedDate, schedTime } = this.state;
    if (schedType !== 'scheduled' || !schedDate) return undefined;
    return new Date(`${schedDate}T${schedTime || '00:00'}:00`).toISOString();
  }

  _buildTemplate() {
    const {
      channel, msgTitle, msgBody, msgDeeplink, msgBtn1, msgBtn2,
      waTemplateName, waTemplateParams, waImageUrl, waIsCustomText, waText,
      smsText, smsTemplateId, emailSubject, emailHtml,
    } = this.state;

    if (channel === 'whatsapp') {
      return waIsCustomText
        ? { isCustomText: true, text: waText }
        : {
            templateName: waTemplateName,
            templateParams: waTemplateParams.split(',').map((s) => s.trim()).filter(Boolean),
            ...(waImageUrl ? { imageUrl: waImageUrl } : {}),
          };
    }
    if (channel === 'sms') {
      return { text: smsText, ...(smsTemplateId ? { templateId: smsTemplateId } : {}) };
    }
    if (channel === 'email') {
      return { subject: emailSubject, html: emailHtml };
    }
    return { title: msgTitle, body: msgBody, deeplink: msgDeeplink, button1: msgBtn1, button2: msgBtn2 };
  }

  async _buildCampaignPayload() {
    const { campName, channel, quietHours, stopOnConvert, freqCap, controlGroup } = this.state;
    const segment_id = await this._resolveSegmentId();
    if (!segment_id) {
      throw new Error('Please select or build an audience first.');
    }
    return {
      name: campName,
      segment_id,
      channel,
      template: this._buildTemplate(),
      delivery_settings: { quietHours, stopOnConvert, freqCap, controlGroup },
      scheduled_at: this._buildScheduledAt(),
    };
  }

  _save() {
    const { editingId } = this.state;
    return this._buildCampaignPayload().then((payload) => (
      editingId ? campaignService.update(editingId, payload) : campaignService.create(payload)
    ));
  }

  draft() {
    const { campName, editingId } = this.state;
    if (!campName.trim()) {
      EventBus.emit('toast', { type: 'error', message: 'Please enter a campaign name.' });
      return;
    }
    this.setState({ saving: true });
    this._save()
      .then(() => {
        this.setState({ saving: false, open: false });
        EventBus.emit('toast', { type: 'info', message: editingId ? 'Campaign updated.' : 'Draft saved.' });
      })
      .catch((err) => {
        this.setState({ saving: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to save draft.' });
      });
  }

  launch() {
    const { campName, schedType, editingId, editingLaunchedAt } = this.state;
    if (!campName.trim()) {
      EventBus.emit('toast', { type: 'error', message: 'Please enter a campaign name.' });
      return;
    }
    this.setState({ saving: true });
    this._save()
      .then((campaign) => {
        // Already launched before (paused mid-send) — don't re-trigger a
        // launch from here, that's what the dedicated Resume action is for.
        if (editingLaunchedAt) return campaign;
        // 'now' and 'optimal' both send immediately — there's no send-time
        // optimization model yet, so 'optimal' is treated the same as 'now'.
        if (schedType !== 'scheduled') return campaignService.launch(campaign.id);
        return campaign;
      })
      .then(() => {
        this.setState({ saving: false, open: false });
        EventBus.emit('toast', {
          type: 'success',
          message: editingId ? `Campaign "${campName}" updated.` : `Campaign "${campName}" launched!`,
        });
      })
      .catch((err) => {
        this.setState({ saving: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to launch campaign.' });
      });
  }

  render() {
    const {
      open, step, saving, editingId, initialSegment, audience, channel,
      msgTitle, msgBody, msgDeeplink, msgBtn1, msgBtn2,
      waTemplateName, waTemplateParams, waImageUrl, waIsCustomText, waText,
      smsText, smsTemplateId, emailSubject, emailHtml,
      schedType, schedDate, schedTime, schedTZ, quietHours, stopOnConvert, freqCap, controlGroup, campName,
    } = this.state;

    const isLast = step === STEPS.length - 1;
    const pct    = ((step + 1) / STEPS.length) * 100;

    return (
      <>
        <Modal
          open={open}
          onClose={() => this.setState({ open: false })}
          width="720px"
          closable={false}
          bodyPadding={false}
        >
          {/* Wizard header — title/close on their own row so the labeled
              5-step Stepper (which needs ~550px on its own) always has room,
              rather than fighting the title and close button for space on
              one row (that combination doesn't fit in a 720px modal). */}
          <div className={styles.wHead}>
            <div className={styles.wHeadTop}>
              <div className={styles.wHeadLeft}>
                <div className={styles.wStep}>Step {step + 1} of {STEPS.length}</div>
                <div className={styles.wTitle}>
                  {editingId ? 'Edit campaign — ' : ''}
                  {['Select your audience','Choose channel','Compose message','Set schedule','Name & launch'][step]}
                </div>
              </div>
              <button className={styles.wClose} onClick={() => this.setState({ open: false })}><Icon name="close" size={18} /></button>
            </div>
            <Stepper steps={STEPS} current={step} />
          </div>

          {/* Progress bar */}
          <div className={styles.wProgress}>
            <div className={styles.wProgressFill} style={{ width: pct + '%' }} />
          </div>

          {/* Step body */}
          <div className={styles.wBody}>
            {step === 0 && (
              <Step1Audience initialSegment={initialSegment} onChange={aud => this.setState({ audience: aud })} />
            )}
            {step === 1 && <Step2Channel value={channel} onChange={v => this.setState({ channel: v })} />}
            {step === 2 && (
              <Step3Message
                channel={channel}
                msgTitle={msgTitle} msgBody={msgBody} msgDeeplink={msgDeeplink} msgBtn1={msgBtn1} msgBtn2={msgBtn2}
                waTemplateName={waTemplateName} waTemplateParams={waTemplateParams} waImageUrl={waImageUrl} waIsCustomText={waIsCustomText} waText={waText}
                smsText={smsText} smsTemplateId={smsTemplateId}
                emailSubject={emailSubject} emailHtml={emailHtml}
                onField={this.setField}
              />
            )}
            {step === 3 && (
              <Step4Schedule
                schedType={schedType} schedDate={schedDate} schedTime={schedTime} schedTZ={schedTZ}
                quietHours={quietHours} stopOnConvert={stopOnConvert} freqCap={freqCap} controlGroup={controlGroup}
                onField={this.setField}
              />
            )}
            {step === 4 && (
              <Step5Review
                campName={campName} audience={audience} channel={channel}
                msgTitle={msgTitle} waTemplateName={waTemplateName} smsText={smsText} emailSubject={emailSubject}
                schedType={schedType}
                onField={this.setField}
              />
            )}
          </div>

          {/* Footer */}
          <div className={styles.wFoot}>
            <div>
              <button className={styles.ghostBtn} onClick={this.draft}>Save draft</button>
            </div>
            <div className={styles.wFootRight}>
              {step > 0 && (
                <button className={styles.ghostBtn} onClick={this.back}>← Back</button>
              )}
              {!isLast && (
                <button className={styles.nextBtn} onClick={this.next}>Continue →</button>
              )}
              {isLast && (
                <button className={styles.launchBtn} disabled={saving} onClick={this.launch}>
                  {saving
                    ? 'Saving…'
                    : editingId
                      ? <><Icon name="save" size={15} /> Save Changes</>
                      : <><Icon name="rocket_launch" size={15} /> Launch Campaign</>}
                </button>
              )}
            </div>
          </div>
        </Modal>
      </>
    );
  }
}

export default CampaignWizard;
